import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Square,
  DoorOpen,
  DoorClosed,
  MoveHorizontal,
  UserRound,
  Mail,
  Phone,
  X,
  CheckCircle2,
  Leaf,
  MapPin,
  Building2,
  Home,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { useQuotes } from '../context/QuotesContext';
import ContactSearchPicker from '../components/ContactSearchPicker';
import {
  buildConfiguratorNavigationState,
  buildDuurzaamadviesloketContact,
  buildSelectedContactFromClientDetails,
  clearActiveContactForConfigurator,
  persistActiveContactForConfigurator,
  resolveConfiguratorContactState,
  applyExplicitContactToBackendQuoteSession,
  isDuurzaamContact,
  DUURZAAM_PARTNER_LABEL,
  getDuurzaamDefaultFormValues,
} from '../lib/contactConfigurator';
import { isEditingBackendQuote } from '../lib/quoteSession';

const getContactInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const ConfiguratorSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { activeBackendQuote, patchActiveBackendQuote } = useQuotes();
  const returnTo = location.state?.returnTo;
  const [activeContact, setActiveContact] = useState(null);
  const [contactMode, setContactMode] = useState('crm'); // 'crm' | 'duurzaam'
  const [duurzaamForm, setDuurzaamForm] = useState(getDuurzaamDefaultFormValues);
  const [duurzaamError, setDuurzaamError] = useState('');
  const [duurzaamEditing, setDuurzaamEditing] = useState(true);

  const syncFromContact = (contact) => {
    if (isDuurzaamContact(contact)) {
      setContactMode('duurzaam');
      setDuurzaamForm({
        endClientName: contact.endClientName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        address: contact.address || '',
        postalCode: contact.postalCode || '',
        city: contact.city || '',
      });
      setDuurzaamEditing(false);
      return;
    }
    setContactMode('crm');
    setDuurzaamForm(getDuurzaamDefaultFormValues());
    setDuurzaamEditing(true);
  };

  useEffect(() => {
    if (authLoading) return;

    // New offer: always start with an empty contact picker
    if (location.state?.newQuote) {
      clearActiveContactForConfigurator();
      setActiveContact(null);
      setContactMode('crm');
      setDuurzaamForm(getDuurzaamDefaultFormValues());
      setDuurzaamEditing(true);
      setDuurzaamError('');
      return;
    }

    // Explicit contact from navigation (Contact Profile, Add drawing) wins
    const fromState = location.state?.selectedContact;
    if (fromState?.name || fromState?.email || isDuurzaamContact(fromState)) {
      setActiveContact(fromState);
      syncFromContact(fromState);
      return;
    }

    // Editing / adding a drawing without an explicit contact → use the quote's client
    if (location.state?.preserveQuoteSession || isEditingBackendQuote(activeBackendQuote)) {
      const fromQuote = buildSelectedContactFromClientDetails(activeBackendQuote?.clientDetails);
      if (fromQuote) {
        persistActiveContactForConfigurator(fromQuote, user?.id);
        setActiveContact(fromQuote);
        syncFromContact(fromQuote);
        return;
      }
    }

    const resolved = resolveConfiguratorContactState(location.state, user?.id);
    setActiveContact(resolved);
    syncFromContact(resolved);
    // Only re-resolve on navigation/auth — not when the user picks a contact (that patches the quote).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, location.state, user?.id]);

  const handleBack = () => {
    if (location.state?.fromContactProfile && returnTo) {
      navigate(returnTo);
      return;
    }
    navigate('/');
  };

  const applyContact = (payload) => {
    if (!payload) return;
    const saved = persistActiveContactForConfigurator(payload, user?.id);
    setActiveContact(saved);
    const patch = applyExplicitContactToBackendQuoteSession(activeBackendQuote, saved, {
      isEditingExisting: isEditingBackendQuote(activeBackendQuote),
    });
    if (patch) {
      patchActiveBackendQuote(patch);
    }
  };

  const handleContactSelect = (contact) => {
    setContactMode('crm');
    setDuurzaamError('');
    setDuurzaamForm(getDuurzaamDefaultFormValues());
    setDuurzaamEditing(true);
    applyContact(contact);
  };

  const handleClearContact = () => {
    clearActiveContactForConfigurator();
    setActiveContact(null);
    setDuurzaamForm(getDuurzaamDefaultFormValues());
    setDuurzaamEditing(true);
    setDuurzaamError('');
  };

  const handleSelectMode = (mode) => {
    setContactMode(mode);
    setDuurzaamError('');
    if (mode === 'crm') {
      if (isDuurzaamContact(activeContact)) {
        handleClearContact();
      }
      return;
    }
    // Switch to Duurzaam
    if (activeContact && !isDuurzaamContact(activeContact)) {
      clearActiveContactForConfigurator();
      setActiveContact(null);
      setDuurzaamForm(getDuurzaamDefaultFormValues());
      setDuurzaamEditing(true);
      return;
    }
    if (isDuurzaamContact(activeContact)) {
      syncFromContact(activeContact);
      return;
    }
    setDuurzaamForm(getDuurzaamDefaultFormValues());
    setDuurzaamEditing(true);
  };

  const handleDuurzaamFieldChange = (field, value) => {
    setDuurzaamForm((prev) => ({ ...prev, [field]: value }));
    setDuurzaamError('');
  };

  const handleApplyDuurzaam = () => {
    const payload = buildDuurzaamadviesloketContact(duurzaamForm);
    if (!payload) {
      setDuurzaamError(t('configurator.duurzaamNameRequired'));
      return;
    }
    setDuurzaamError('');
    applyContact(payload);
    setDuurzaamForm({
      endClientName: payload.endClientName,
      email: payload.email,
      phone: payload.phone,
      address: payload.address,
      postalCode: payload.postalCode,
      city: payload.city,
    });
    setDuurzaamEditing(false);
  };

  const handleEditDuurzaam = () => {
    if (isDuurzaamContact(activeContact)) {
      setDuurzaamForm({
        endClientName: activeContact.endClientName || '',
        email: activeContact.email || '',
        phone: activeContact.phone || '',
        address: activeContact.address || '',
        postalCode: activeContact.postalCode || '',
        city: activeContact.city || '',
      });
    }
    setDuurzaamEditing(true);
  };

  const handleSelectConfigurator = (path) => {
    // Auto-apply duurzaam form if user filled name but didn't click Apply
    let contactForNav = activeContact;
    if (contactMode === 'duurzaam' && (duurzaamForm.endClientName || '').trim()) {
      const payload = buildDuurzaamadviesloketContact(duurzaamForm);
      if (payload) {
        contactForNav = persistActiveContactForConfigurator(payload, user?.id);
        setActiveContact(contactForNav);
        setDuurzaamEditing(false);
        const patch = applyExplicitContactToBackendQuoteSession(activeBackendQuote, contactForNav, {
          isEditingExisting: isEditingBackendQuote(activeBackendQuote),
        });
        if (patch) {
          patchActiveBackendQuote(patch);
        }
      }
    }

    navigate(path, {
      state: buildConfiguratorNavigationState(
        location.state,
        { selectedContact: contactForNav },
        user?.id
      ),
    });
  };

  const duurzaamPreviewName = useMemo(() => {
    const end = (duurzaamForm.endClientName || '').trim();
    return end ? `${DUURZAAM_PARTNER_LABEL} / ${end}` : DUURZAAM_PARTNER_LABEL;
  }, [duurzaamForm.endClientName]);

  const configuratorTypes = [
    {
      id: 'windows',
      nameKey: 'configurator.kozijnen',
      descriptionKey: 'configurator.kozijnenDesc',
      icon: Square,
      color: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      path: '/kozijnen',
    },
    {
      id: 'patio-door',
      nameKey: 'configurator.terrasdeur',
      descriptionKey: 'configurator.terrasdeurDesc',
      icon: DoorOpen,
      color: 'bg-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-green-500',
      path: '/terrasdeur',
    },
    {
      id: 'back-door',
      nameKey: 'configurator.achterdeur',
      descriptionKey: 'configurator.achterdeurDesc',
      icon: DoorClosed,
      color: 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-500',
      path: '/achterdeur',
    },
    {
      id: 'front-door',
      nameKey: 'configurator.voordeur',
      descriptionKey: 'configurator.voordeurDesc',
      icon: DoorClosed,
      color: 'bg-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-500',
      path: '/voordeur',
    },
    {
      id: 'sliding-door',
      nameKey: 'configurator.schuifpui',
      descriptionKey: 'configurator.schuifpuiDesc',
      icon: MoveHorizontal,
      color: 'bg-teal-100',
      iconColor: 'text-teal-600',
      borderColor: 'border-teal-500',
      path: '/schuifpui',
    },
    {
      id: 'keralit',
      nameKey: 'configurator.keralit',
      descriptionKey: 'configurator.keralitDesc',
      icon: Square,
      color: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      borderColor: 'border-indigo-500',
      path: '/keralit',
    },
    {
      id: 'plastisol-zetwerk',
      nameKey: 'configurator.plastisol',
      descriptionKey: 'configurator.plastisolDesc',
      image: `${process.env.PUBLIC_URL || ''}/images/plastisol-zetwerk.png`,
      color: 'bg-stone-100',
      borderColor: 'border-stone-500',
      path: '/plastisol-zetwerk',
    },
    {
      id: 'dakkapel',
      nameKey: 'configurator.dakkapel',
      descriptionKey: 'configurator.dakkapelDesc',
      icon: Home,
      color: 'bg-sky-100',
      iconColor: 'text-sky-600',
      borderColor: 'border-sky-500',
      path: '/dakkapel',
    },
  ];

  const showCrmActive = contactMode === 'crm' && activeContact?.name && !isDuurzaamContact(activeContact);
  const showDuurzaamActive =
    contactMode === 'duurzaam' && isDuurzaamContact(activeContact) && !duurzaamEditing;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">
                {location.state?.fromContactProfile && returnTo
                  ? t('contactProfile.backToContact')
                  : t('configurator.backToHome')}
              </span>
            </button>
            <button
              type="button"
              onClick={() =>
                navigate('/subsidiechecker', {
                  state: buildConfiguratorNavigationState(
                    location.state,
                    { from: location.pathname, preserveQuoteSession: true },
                    user?.id
                  ),
                })
              }
              className="px-3 py-1.5 border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800 rounded-lg text-sm font-medium transition-colors"
            >
              {t('configurator.subsidieChecker')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('configurator.selectType')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('configurator.selectTypeDescription')}
          </p>
        </div>

        {isAuthenticated && !authLoading ? (
          <div className="mb-10 mx-auto max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-gray-100 bg-slate-50/80 px-6 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                <UserRound className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900">
                  {t('configurator.selectContact')}
                </h2>
                <p className="text-xs text-gray-500">
                  {t('configurator.selectContactDescription')}
                </p>
              </div>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Mode switch */}
              <div
                className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1"
                role="tablist"
                aria-label={t('configurator.selectContact')}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={contactMode === 'crm'}
                  onClick={() => handleSelectMode('crm')}
                  className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                    contactMode === 'crm'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Building2 className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t('configurator.contactModeCrm')}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={contactMode === 'duurzaam'}
                  onClick={() => handleSelectMode('duurzaam')}
                  className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                    contactMode === 'duurzaam'
                      ? 'bg-white text-emerald-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Leaf className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t('configurator.contactModeDuurzaam')}</span>
                </button>
              </div>

              {contactMode === 'crm' && showCrmActive ? (
                <div key="crm-active" className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-orange-200 bg-orange-500 text-sm font-semibold text-white">
                      {getContactInitials(activeContact.name)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-base font-semibold text-gray-900">
                          {activeContact.name}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('configurator.contactLinkedBadge')}
                        </span>
                      </div>

                      <div className="mt-1.5 space-y-0.5 text-sm text-gray-600">
                        {activeContact.email ? (
                          <p className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">{activeContact.email}</span>
                          </p>
                        ) : null}
                        {activeContact.phone ? (
                          <p className="flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">{activeContact.phone}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleClearContact}
                      className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-orange-100 hover:text-orange-700"
                      aria-label={t('configurator.clearContact')}
                      title={t('configurator.clearContact')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-4">
                    <ContactSearchPicker
                      key={`crm-picker-${activeContact.id || activeContact.email || activeContact.name}`}
                      selectedContact={activeContact}
                      onSelect={handleContactSelect}
                      buttonClassName="border-orange-200 bg-white/70 hover:bg-white"
                    />
                  </div>
                </div>
              ) : null}

              {contactMode === 'crm' && !showCrmActive ? (
                <div key="crm-empty">
                  <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                      <UserRound className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">
                        {t('configurator.noContactSelectedTitle')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('configurator.noContactSelectedHint')}
                      </p>
                    </div>
                  </div>
                  <ContactSearchPicker
                    key="crm-picker-empty"
                    selectedContact={activeContact}
                    onSelect={handleContactSelect}
                  />
                </div>
              ) : null}

              {contactMode === 'duurzaam' && showDuurzaamActive ? (
                <div key="duurzaam-active" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-600 text-sm font-semibold text-white">
                      {getContactInitials(activeContact.endClientName || activeContact.name)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-base font-semibold text-gray-900">
                          {activeContact.name}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('configurator.contactLinkedBadge')}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs font-medium text-emerald-700">
                        {DUURZAAM_PARTNER_LABEL}
                      </p>

                      <div className="mt-1.5 space-y-0.5 text-sm text-gray-600">
                        {activeContact.email ? (
                          <p className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">{activeContact.email}</span>
                          </p>
                        ) : null}
                        {activeContact.phone ? (
                          <p className="flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">{activeContact.phone}</span>
                          </p>
                        ) : null}
                        {(activeContact.address || activeContact.city || activeContact.postalCode) ? (
                          <p className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">
                              {[activeContact.address, activeContact.postalCode, activeContact.city]
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleClearContact}
                      className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-emerald-100 hover:text-emerald-700"
                      aria-label={t('configurator.clearContact')}
                      title={t('configurator.clearContact')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleEditDuurzaam}
                      className="h-10 w-full justify-between border-emerald-200 bg-white/70 hover:bg-white text-slate-700"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Leaf className="h-4 w-4 shrink-0 text-emerald-600" />
                        <span className="truncate">{activeContact.endClientName || activeContact.name}</span>
                      </span>
                      <span className="text-xs font-medium text-emerald-700 shrink-0">
                        {t('configurator.changeContact')}
                      </span>
                    </Button>
                  </div>
                </div>
              ) : null}

              {contactMode === 'duurzaam' && !showDuurzaamActive ? (
                <div
                  key="duurzaam-form"
                  className="overflow-hidden rounded-xl border border-emerald-200/80 bg-gradient-to-b from-emerald-50/80 to-white"
                >
                  <div className="flex items-start gap-3 border-b border-emerald-100 px-4 py-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
                      <Leaf className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-emerald-950">
                        {t('configurator.duurzaamTitle')}
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-emerald-800/70">
                        {t('configurator.duurzaamDescription')}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 px-4 py-4">
                    <div className="rounded-lg border border-dashed border-emerald-200 bg-white/60 px-3 py-2">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-700/70">
                        {t('configurator.duurzaamQuotePreview')}
                      </p>
                      <p className="mt-0.5 truncate text-sm text-slate-600">
                        {t('quote.template.metaFor')}{' '}
                        <span className="font-medium text-slate-900">{duurzaamPreviewName}</span>
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label htmlFor="duurzaam-name" className="text-xs font-medium text-slate-700">
                          {t('configurator.duurzaamClientName')} *
                        </Label>
                        <Input
                          id="duurzaam-name"
                          value={duurzaamForm.endClientName}
                          onChange={(e) => handleDuurzaamFieldChange('endClientName', e.target.value)}
                          placeholder={t('configurator.duurzaamClientNamePlaceholder')}
                          className="h-10 border-emerald-200/80 bg-white focus-visible:ring-emerald-500/30"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="duurzaam-email" className="text-xs font-medium text-slate-700">
                          {t('configurator.duurzaamEmail')}
                        </Label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="duurzaam-email"
                            type="email"
                            value={duurzaamForm.email}
                            onChange={(e) => handleDuurzaamFieldChange('email', e.target.value)}
                            placeholder={t('configurator.duurzaamEmailPlaceholder')}
                            className="h-10 border-emerald-200/80 bg-white pl-9 focus-visible:ring-emerald-500/30"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="duurzaam-phone" className="text-xs font-medium text-slate-700">
                          {t('configurator.duurzaamPhone')}
                        </Label>
                        <div className="relative">
                          <Phone className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="duurzaam-phone"
                            value={duurzaamForm.phone}
                            onChange={(e) => handleDuurzaamFieldChange('phone', e.target.value)}
                            placeholder={t('configurator.duurzaamPhonePlaceholder')}
                            className="h-10 border-emerald-200/80 bg-white pl-9 focus-visible:ring-emerald-500/30"
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label htmlFor="duurzaam-address" className="text-xs font-medium text-slate-700">
                          {t('configurator.duurzaamAddress')}
                        </Label>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="duurzaam-address"
                            value={duurzaamForm.address}
                            onChange={(e) => handleDuurzaamFieldChange('address', e.target.value)}
                            placeholder={t('configurator.duurzaamAddressPlaceholder')}
                            className="h-10 border-emerald-200/80 bg-white pl-9 focus-visible:ring-emerald-500/30"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="duurzaam-postal" className="text-xs font-medium text-slate-700">
                          {t('configurator.duurzaamPostalCode')}
                        </Label>
                        <Input
                          id="duurzaam-postal"
                          value={duurzaamForm.postalCode}
                          onChange={(e) => handleDuurzaamFieldChange('postalCode', e.target.value)}
                          placeholder={t('configurator.duurzaamPostalCodePlaceholder')}
                          className="h-10 border-emerald-200/80 bg-white focus-visible:ring-emerald-500/30"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="duurzaam-city" className="text-xs font-medium text-slate-700">
                          {t('configurator.duurzaamCity')}
                        </Label>
                        <Input
                          id="duurzaam-city"
                          value={duurzaamForm.city}
                          onChange={(e) => handleDuurzaamFieldChange('city', e.target.value)}
                          placeholder={t('configurator.duurzaamCityPlaceholder')}
                          className="h-10 border-emerald-200/80 bg-white focus-visible:ring-emerald-500/30"
                        />
                      </div>
                    </div>

                    {duurzaamError ? (
                      <p className="text-sm text-red-600">{duurzaamError}</p>
                    ) : null}

                    <div className="flex justify-end gap-2">
                      {isDuurzaamContact(activeContact) ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDuurzaamEditing(false)}
                        >
                          {t('configurator.duurzaamCancelEdit')}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        onClick={handleApplyDuurzaam}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      >
                        {isDuurzaamContact(activeContact)
                          ? t('configurator.duurzaamUpdate')
                          : t('configurator.duurzaamApply')}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {configuratorTypes.map((type) => {
            const Icon = type.icon;
            return (
              <Card
                key={type.id}
                className="group cursor-pointer border-2 hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={() => handleSelectConfigurator(type.path)}
              >
                <CardHeader className="text-center pb-4">
                  <div className={`w-20 h-20 ${type.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform overflow-hidden`}>
                    {type.image ? (
                      <img
                        src={type.image}
                        alt=""
                        className="w-full h-full object-contain p-2"
                        draggable={false}
                      />
                    ) : (
                      <Icon className={`w-10 h-10 ${type.iconColor}`} />
                    )}
                  </div>
                  <CardTitle className="text-2xl mb-2">{t(type.nameKey)}</CardTitle>
                  <CardDescription className="text-base">
                    {t(type.descriptionKey)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-slate-800 hover:bg-slate-900 group-hover:bg-orange-600 transition-colors"
                    size="lg"
                  >
                    {t('configurator.configureNow')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="bg-white rounded-xl p-8 border-2 border-gray-200 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            {t('configurator.notSure')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('configurator.helpDescription')}
          </p>
          <Button variant="outline" size="lg" className="border-2">
            {t('configurator.contactSupport')}
          </Button>
        </div>
      </div>

      <div className="bg-slate-800 py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">5</div>
              <div className="text-slate-300">{t('configurator.productTypes')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">3 Min</div>
              <div className="text-slate-300">{t('configurator.averageConfiguration')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">100%</div>
              <div className="text-slate-300">{t('configurator.accuracyGuaranteed')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguratorSelection;
